import { useMemo, useState, useRef, useCallback } from 'react';
import { QuotaData, getQuotaGroup, getFamilyDisplayName } from './quotaUtils';

interface Props {
  quotas: QuotaData[];
  onCellClick: (provider: string) => void;
  onRequestIncrease?: (quota: QuotaData, groupLabel?: string, groupFamilies?: string[]) => void;
  providerStatus?: { [key: string]: { authenticated: boolean; error: string | null; has_data: boolean } };
}

const providerOrder = ['aws', 'gcp', 'azure'];

const regionOrder = [
  'us-east-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 'ap-southeast-1', 'ap-northeast-1',
  'us-central1', 'us-east1', 'europe-west1', 'asia-east1',
  'eastus', 'eastus2',
];

interface HoveredCell {
  provider: string;
  region: string;
  rect: DOMRect;
}

export default function QuotaHeatmap({ quotas, onCellClick, onRequestIncrease, providerStatus = {} }: Props) {
  const [hoveredCell, setHoveredCell] = useState<HoveredCell | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Create heatmap data structure
  const heatmapData = useMemo(() => {
    const data: Record<string, Record<string, number>> = {};

    // Initialize with all providers and regions
    providerOrder.forEach(provider => {
      data[provider] = {};
    });

    // Group quotas and calculate average utilization
    quotas.forEach(quota => {
      if (!data[quota.provider]) {
        data[quota.provider] = {};
      }

      if (!data[quota.provider][quota.region]) {
        const regionQuotas = quotas.filter(q => q.provider === quota.provider && q.region === quota.region);
        const avgUtilization = regionQuotas.reduce((sum, q) => sum + q.utilization_percent, 0) / regionQuotas.length;
        data[quota.provider][quota.region] = avgUtilization;
      }
    });

    return data;
  }, [quotas]);

  // Index quotas by provider+region for popover
  const quotasByCell = useMemo(() => {
    const map: Record<string, QuotaData[]> = {};
    for (const q of quotas) {
      const key = `${q.provider}-${q.region}`;
      if (!map[key]) map[key] = [];
      map[key].push(q);
    }
    // Sort each region's families by utilization desc
    for (const arr of Object.values(map)) {
      arr.sort((a, b) => b.utilization_percent - a.utilization_percent);
    }
    return map;
  }, [quotas]);

  // Get all unique regions from the data
  const allRegions = useMemo(() => {
    const regions = new Set<string>();
    Object.values(heatmapData).forEach(providerData => {
      Object.keys(providerData).forEach(region => regions.add(region));
    });

    return Array.from(regions).sort((a, b) => {
      const aIndex = regionOrder.indexOf(a);
      const bIndex = regionOrder.indexOf(b);

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [heatmapData]);

  const getColorForUtilization = (utilization: number | undefined) => {
    if (utilization === undefined) return 'bg-gray-900';
    if (utilization >= 90) return 'bg-red-600 hover:bg-red-500';
    if (utilization >= 80) return 'bg-yellow-600 hover:bg-yellow-500';
    if (utilization >= 70) return 'bg-yellow-700 hover:bg-yellow-600';
    if (utilization >= 60) return 'bg-green-700 hover:bg-green-600';
    if (utilization >= 40) return 'bg-green-600 hover:bg-green-500';
    if (utilization >= 20) return 'bg-green-500 hover:bg-green-400';
    return 'bg-green-400 hover:bg-green-300';
  };

  const getTextColorForUtilization = (utilization: number | undefined) => {
    if (utilization === undefined) return 'text-gray-600';
    if (utilization >= 60) return 'text-white';
    return 'text-gray-900';
  };

  const providerLogos: Record<string, string> = {
    aws: '/icons/icons8-aws-48.svg',
    gcp: '/icons/icons8-google-cloud-48.svg',
    azure: '/icons/azure-48.svg',
  };

  const providerNames: Record<string, string> = {
    aws: 'AWS',
    gcp: 'GCP',
    azure: 'Azure',
  };

  const handleCellEnter = useCallback((provider: string, region: string, e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHoveredCell({ provider, region, rect });
  }, []);

  const handleCellLeave = useCallback((e: React.MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null;
    if (popoverRef.current?.contains(related)) return;
    setHoveredCell(null);
  }, []);

  const handlePopoverLeave = useCallback((e: React.MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null;
    // Check if moving back to the cell that triggered it
    if (related?.closest('[data-heatmap-cell]')) return;
    setHoveredCell(null);
  }, []);

  const handleRequestForQuota = useCallback((quota: QuotaData) => {
    if (!onRequestIncrease) return;
    const group = getQuotaGroup(quota.provider, quota.instance_family);
    if (group) {
      onRequestIncrease(quota, group.label, group.families);
    } else {
      onRequestIncrease(quota);
    }
  }, [onRequestIncrease]);

  return (
    <div ref={containerRef} className="bg-gray-900 rounded-xl p-6 border border-gray-800 relative">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Quota Utilization Heatmap</h3>
        <p className="text-gray-400 text-sm">Average utilization by provider and region. Hover for details.</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left p-2 text-gray-400 font-medium">Provider</th>
              {allRegions.map(region => (
                <th key={region} className="text-center p-2 text-gray-400 font-medium text-xs">
                  <div className="transform -rotate-45 origin-center whitespace-nowrap">
                    {region}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {providerOrder.filter(provider => heatmapData[provider]).map(provider => {
              const status = providerStatus[provider];
              const isAuthenticated = status?.authenticated ?? true;

              return (
                <tr key={provider}>
                  <td className="p-2">
                    <button
                      onClick={() => isAuthenticated ? onCellClick(provider) : null}
                      className={`flex items-center space-x-2 rounded-lg p-2 transition-colors ${
                        isAuthenticated ? 'hover:bg-gray-800' : 'opacity-50 cursor-not-allowed'
                      }`}
                      title={!isAuthenticated ? 'Credentials not configured' : ''}
                    >
                    <img
                      src={providerLogos[provider]}
                      alt={provider}
                      className="w-6 h-6 object-contain"
                    />
                    <span className={`font-medium ${isAuthenticated ? 'text-white' : 'text-gray-500'}`}>
                      {providerNames[provider]}
                      {!isAuthenticated && (
                        <span className="text-xs text-red-400 ml-2">(No credentials)</span>
                      )}
                    </span>
                  </button>
                </td>
                {allRegions.map(region => {
                  const utilization = heatmapData[provider]?.[region];
                  const hasData = utilization !== undefined;

                  return (
                    <td key={`${provider}-${region}`} className="p-1">
                      {hasData ? (
                        <button
                          data-heatmap-cell
                          onClick={() => onCellClick(provider)}
                          onMouseEnter={(e) => handleCellEnter(provider, region, e)}
                          onMouseLeave={handleCellLeave}
                          className={`w-12 h-12 rounded-lg flex items-center justify-center text-xs font-bold transition-all cursor-pointer relative ${getColorForUtilization(
                            utilization
                          )} ${getTextColorForUtilization(utilization)}`}
                          title={`${provider.toUpperCase()} - ${region}: ${Math.round(utilization)}%`}
                        >
                          {Math.round(utilization)}
                          {utilization >= 80 && onRequestIncrease && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                              <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-900 border border-gray-800" />
                      )}
                    </td>
                  );
                })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Hover Popover */}
      {hoveredCell && (() => {
        const cellQuotas = quotasByCell[`${hoveredCell.provider}-${hoveredCell.region}`];
        if (!cellQuotas || cellQuotas.length === 0) return null;

        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return null;

        const popoverLeft = hoveredCell.rect.left - containerRect.left + hoveredCell.rect.width / 2;
        const popoverTop = hoveredCell.rect.bottom - containerRect.top + 8;

        // Find highest-utilization family for the CTA
        const topQuota = cellQuotas[0];

        return (
          <div
            ref={popoverRef}
            className="absolute z-50"
            style={{
              left: `${popoverLeft}px`,
              top: `${popoverTop}px`,
              transform: 'translateX(-50%)',
            }}
            onMouseLeave={handlePopoverLeave}
          >
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="w-2 h-2 bg-gray-950 border-t border-l border-gray-700 transform rotate-45 -mb-1 relative z-10" />
            </div>
            <div className="bg-gray-950 border border-gray-700 rounded-lg shadow-xl px-4 py-3 min-w-[240px] max-w-[300px]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-semibold text-sm">{hoveredCell.region}</span>
                <span className="text-gray-500 text-xs">{hoveredCell.provider.toUpperCase()}</span>
              </div>

              <div className="space-y-1.5 mb-3">
                {cellQuotas.slice(0, 5).map(q => {
                  const utilColor = q.utilization_percent >= 90
                    ? 'text-red-400'
                    : q.utilization_percent >= 70
                      ? 'text-yellow-400'
                      : 'text-green-400';

                  return (
                    <div key={q.instance_family} className="flex items-center justify-between text-xs">
                      <span className="text-gray-300">{getFamilyDisplayName(q.provider, q.instance_family)}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-400">{q.current_usage}/{q.quota_limit}</span>
                        <span className={`font-medium ${utilColor}`}>
                          {Math.round(q.utilization_percent)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
                {cellQuotas.length > 5 && (
                  <div className="text-xs text-gray-500">+{cellQuotas.length - 5} more</div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                <button
                  onClick={() => onCellClick(hoveredCell.provider)}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  View Details
                </button>
                {onRequestIncrease && topQuota && !topQuota.quota_increase_requested && (
                  <button
                    onClick={() => handleRequestForQuota(topQuota)}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      topQuota.utilization_percent >= 80
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600'
                    }`}
                  >
                    Request Increase
                  </button>
                )}
                {topQuota?.quota_increase_requested && (
                  <span className="text-xs text-yellow-300 flex items-center space-x-1">
                    <svg className="w-2.5 h-2.5 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
                      <circle cx="4" cy="4" r="3" />
                    </svg>
                    <span>Pending</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center space-x-4">
        <span className="text-gray-400 text-sm">Utilization:</span>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-green-400 rounded"></div>
            <span className="text-xs text-gray-400">0-20%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-xs text-gray-400">20-40%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-green-600 rounded"></div>
            <span className="text-xs text-gray-400">40-60%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-yellow-700 rounded"></div>
            <span className="text-xs text-gray-400">60-70%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-yellow-600 rounded"></div>
            <span className="text-xs text-gray-400">70-80%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-xs text-gray-400">80-90%</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-red-600 rounded"></div>
            <span className="text-xs text-gray-400">90-100%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
